/** @jsxImportSource @emotion/react */

import { FC } from 'react';
import { Tooltip } from 'components';
import { breakpoints, Labels } from '@zip/business-components';
import * as styles from './ProductStatusLabel.styles';
import { ProductConfig } from 'types/interfaces/merchant/ProductConfig';
import { dateFormatter, dateToIso } from 'utils';
import { useMediaQuery } from '@mui/material';
import { theme } from 'global';

type StatusLabel = 'Promotion' | 'Standard' | 'Advertised';
type StatusLabelStyle = 'primary' | 'success' | 'info';

const ProductStatusLabels: FC<{ product: ProductConfig }> = ({ product }) => {
  const statusList: {
    text: StatusLabel;
    variant: StatusLabelStyle;
  }[] = [];

  const isMobile = useMediaQuery(breakpoints.down(theme.breaks.sm));
  const statusTooltip = new Map<StatusLabel, string>([
    [
      'Advertised',
      'This product is used to promote your offering on general landing pages.',
    ],
    [
      'Standard',
      'Default offer chosen inside of the designated transaction limits.',
    ],
    [
      'Promotion',
      !product.PromotionDateRange
        ? 'Offer is currently live.'
        : `Offer is live from ${dateFormatter(
            dateToIso(product.PromotionDateRange[0]),
            'DDMMYYYY'
          )} to ${dateFormatter(
            dateToIso(product.PromotionDateRange[1]),
            'DDMMYYYY'
          )}.`,
    ],
  ]);

  if (product.Promotion) {
    statusList.push({ text: 'Promotion', variant: 'primary' });
  }
  if (product.Standard) {
    statusList.push({ text: 'Standard', variant: 'info' });
  }
  if (product.Advertise) {
    statusList.push({ text: 'Advertised', variant: 'success' });
  }

  return (
    <div id="labelContainer" css={styles.labelContainer}>
      {statusList.map((s, idx) => {
        const tooltipText = statusTooltip.get(s.text);
        return (
          <Tooltip
            key={`product-status-label-${idx}`}
            content={
              <div>
                <div className="body1" style={{ paddingBottom: '8px' }}>
                  {s.text}
                </div>
                <div className="body4">{tooltipText}</div>
              </div>
            }
            dense
            arrow
            maxWidth={240}
            variant="primary"
            dismissable={isMobile}
            placement="bottom-start"
            css={styles.tooltipWidth}
          >
            <Labels.Basic
              variant={s.variant === 'info' ? null : s.variant}
              css={styles.label(s.variant)}
              label={
                <div className="caption1" css={styles.labelText}>
                  {s.text}
                </div>
              }
            />
          </Tooltip>
        );
      })}
    </div>
  );
};

export default ProductStatusLabels;
